import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useButton, useDispatch, useI18n, useSelector } from '~Hooks';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { Box, Button } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useStaffShifts } from '~Providers/StaffShiftsProvider';
import StaffShiftRow from './Components/StaffShiftRow';
import Loader from '~ComponentsImproved/Loader';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import * as actions from '~actions';
import { handyBreakpoint } from '~Styles/breakpoints';

const Wrapper = styled(Box)`
  flex: 1;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: calc(100vh - 64px);
  position: fixed;
  width: 100%;
  height: 100%;

  ${handyBreakpoint} {
    height: calc(100vh - 52px);
  }
`;

const ScrollWrapper = styled(Box)`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  padding: 20px 40px 0px 20px;
  box-sizing: border-box;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out 0.1s forwards;

  ${handyBreakpoint} {
    padding: 20px 12px 0px 12px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ShiftsWrapper = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;

  position: relative;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );

  background-image: linear-gradient(
    to bottom,
    transparent calc(100% - 20px),
    var(--colorBlueGray1200) 100%
  );
`;

const ShiftsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;

  ${handyBreakpoint} {
    grid-template-columns: 1fr;
  }
`;

const FloatingActions = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  width: 176px;
  z-index: 100;

  bottom: 80px;
`;

const LogoutButton = styled(Button)`
  background-color: var(--colorBlueGray1200);
  color: var(--colorBlueGray400);
  border: 1px solid var(--colorBlueGray800);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 2px 8px;
  height: 50px;

  &:active {
    background-color: var(--colorBlueGray1000);
  }
`;

const StaffName = styled.div`
  color: var(--colorWhite);
  margin-top: 1px;
  font-size: 14px;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

/**
 * Scene listing every staff member at the venue together with their current
 * clock-in / clock-out state. All shift data is sourced from the
 * StaffShiftsProvider via useStaffShifts().
 */
const StaffShifts: FC = () => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const staffShifts = useStaffShifts();

  const staff = useSelector((state) => state.staff);
  const staffs = useSelector((state) => state.companyStaff?.staffs) ?? [];
  const staffId = useSelector((state) => state.staff?.id);

  // Mirrors the Tables Navigation: show the floating button only when the
  // company uses staff codes, and toggle copy/onClick based on login state.
  const hasStaffCodes = staffs.length > 0;

  const logOutButton = useButton({
    onClick: () => dispatch(actions.staffLogout()),
  });

  const logInButton = useButton({
    onClick: () => dispatch(actions.staffLogout()),
  });

  const isHandy = window.innerWidth <= 600;

  /** Alphabetical by name; on handy, pin the logged-in staff to the top. */
  const sortedStaffs = [...staffs].sort((a, b) => {
    if (isHandy && staffId) {
      if (a.id === staffId) return -1;
      if (b.id === staffId) return 1;
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  if (staffShifts.initialLoading) {
    return (
      <Wrapper>
        {!isHandy && <BackTopBar title={i18n('StaffShifts.Timeclock')} dark />}
        <Box
          style={{ height: '50vh' }}
          display="flex"
          alignItems="center"
          flexDirection="column"
          gap="small"
          justifyContent="center">
          <Loader />
          <Paragraph color="var(--colorWhite)" level={3}>
            {i18n('StaffShifts.Loading')}
          </Paragraph>
        </Box>
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper display="flex" flexDirection="column">
        {!isHandy && <BackTopBar title={i18n('StaffShifts.Timeclock')} dark />}

        <ScrollWrapper display="flex" flexDirection="column" gap="large">
          <ShiftsWrapper display="flex" flexDirection="column" gap="small">
            {staffShifts.error && (
              <Paragraph level={3} color="var(--colorStatusRed)">
                {staffShifts.error}
              </Paragraph>
            )}

            <ShiftsGrid>
              {sortedStaffs.map((s) => (
                <StaffShiftRow key={s.id} staff={s} />
              ))}
            </ShiftsGrid>
          </ShiftsWrapper>
        </ScrollWrapper>

        {hasStaffCodes && !isHandy && (
          <FloatingActions>
            {staff ? (
              <LogoutButton {...logOutButton}>
                {i18n('General.Logout')}
                <StaffName>{staff.name}</StaffName>
              </LogoutButton>
            ) : (
              <LogoutButton {...logInButton}>{i18n('Menu.Login')}</LogoutButton>
            )}
          </FloatingActions>
        )}
      </Wrapper>

      {!staffId && <LoginModal />}
    </>
  );
};

export default StaffShifts;
